import type { CSSProperties } from 'react';

interface HazardStripesProps {
  /** Stripe colour. */
  color?: string;
  opacity?: number;
  /** Distance between stripe centres; the stripe itself is half of it. */
  period?: number;
  className?: string;
  style?: CSSProperties;
}

/**
 * The 45° hazard-stripe motif, carried over from the concrete/construction origin.
 *
 * This is the handoff's `repeating-linear-gradient(-45deg, …)` verbatim. React
 * Native has no equivalent, so the mobile app drew an SVG pattern of rotated
 * lines and needed a `useId()` workaround because SVG pattern ids are not scoped
 * per-element — two instances with different colours resolved to whichever
 * mounted first. None of that applies here; it is a background image.
 *
 * Sized by the caller, since it is always a decorative fill of an existing box.
 */
export function HazardStripes({
  color = '#38BDF8',
  opacity = 0.4,
  period = 20,
  className,
  style,
}: HazardStripesProps) {
  const stripe = period / 2;
  const rgba = withAlpha(color, opacity);

  return (
    <div
      aria-hidden
      className={className}
      style={{
        backgroundImage: `repeating-linear-gradient(-45deg, transparent 0 ${stripe}px, ${rgba} ${stripe}px ${period}px)`,
        pointerEvents: 'none',
        ...style,
      }}
    />
  );
}

/** #38BDF8 + 0.4 -> rgba(56,189,248,0.4). Passes through any non-hex value. */
function withAlpha(color: string, alpha: number): string {
  const hex = color.replace('#', '');
  if (!/^[0-9a-f]{6}$/i.test(hex)) return color;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
